import React from "react";
import { motion } from "framer-motion";
import { useDashTheme } from "../hooks/useDashTheme";
import { Card, TrendPill } from "./primitives";

// ── Sparkline ──────────────────────────────────────────────────────────────
// Pure-SVG sparkline with a soft area gradient. No chart lib needed → fast & crisp.
export function Sparkline({ data = [], color, width = 96, height = 32, strokeWidth = 2 }) {
  const t = useDashTheme();
  const gid = React.useId();
  const c = color || t.c.primary;
  if (!data || data.length < 2) return <div style={{ width, height }} />;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = width / (data.length - 1);
  const pts = data.map((v, i) => [i * stepX, height - ((v - min) / range) * (height - strokeWidth) - strokeWidth / 2]);
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `${line} L${width},${height} L0,${height} Z`;
  return (
    <svg width={width} height={height} style={{ display: "block", overflow: "visible" }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={c} stopOpacity="0.22" />
          <stop offset="100%" stopColor={c} stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.path d={area} fill={`url(#${gid})`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} />
      <motion.path
        d={line} fill="none" stroke={c} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.9, ease: t.motion.ease.out }}
      />
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r={2.5} fill={c} />
    </svg>
  );
}

// ── RadialGauge ──────────────────────────────────────────────────────────────
// Single-value ring (0–100). Used for scores / utilization in KPI cards.
export function RadialGauge({ value = 0, size = 56, stroke = 6, color, trackColor, label }) {
  const t = useDashTheme();
  const c = color || t.c.primary;
  const v = Math.max(0, Math.min(100, value));
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - v / 100);
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor || t.c.surfaceMuted} strokeWidth={stroke} />
        <motion.circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={c} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={circ} initial={{ strokeDashoffset: circ }} animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1, ease: t.motion.ease.out }}
        />
      </svg>
      <div style={{
        position: "absolute", inset: 0, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        fontSize: t.font.size.sm, fontWeight: t.font.weight.medium, color: t.c.text,
        fontVariantNumeric: "tabular-nums",
      }}>
        {label != null ? label : Math.round(v)}
      </div>
    </div>
  );
}

// ── AnimatedNumber ────────────────────────────────────────────────────────────
// Counts up to the target value on mount/change for a premium feel.
export function AnimatedNumber({ value = 0, decimals = 0, format }) {
  const [display, setDisplay] = React.useState(0);
  const raf = React.useRef();
  React.useEffect(() => {
    const start = performance.now();
    const from = display;
    const to = Number(value) || 0;
    const dur = 700;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(from + (to - from) * eased);
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const out = format ? format(display) : display.toFixed(decimals);
  return <span style={{ fontVariantNumeric: "tabular-nums" }}>{out}</span>;
}

// ── StatCard ──────────────────────────────────────────────────────────────────
// The hero KPI card. Supports: value, label, icon, trend, and ONE visual
// (sparkline | gauge) on the right. Loading → shimmer skeleton.
export function StatCard({
  label, value, sublabel, icon, trend, trendInvert, trendLabel = "vs prev",
  spark, sparkColor, gauge, gaugeColor, accent, loading, onClick, compact,
}) {
  const t = useDashTheme();
  const ac = accent || t.c.primary;
  const pad = compact ? 12 : 16;
  // Deliberate jump between the 12px label and the number: the size contrast is
  // what stops a row of these reading as flat grey boxes.
  const valueSize = compact ? 21 : 27;

  if (loading) {
    return (
      <Card padding={pad}>
        <div style={{ display: "flex", flexDirection: "column", gap: compact ? 10 : 14 }}>
          <Shimmer w={90} h={12} />
          <Shimmer w={110} h={compact ? 24 : 30} />
          <Shimmer w={60} h={11} />
        </div>
      </Card>
    );
  }

  return (
    <Card interactive={!!onClick} onClick={onClick} padding={pad} style={{ overflow: "hidden", position: "relative", height: "100%" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: compact ? 6 : 8 }}>
            <span style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 22, height: 22, borderRadius: t.r.sm, background: `${ac}1F`, color: ac,
            }}>{icon}</span>
            <span style={{
              fontSize: t.font.size.sm, fontWeight: t.font.weight.medium,
              color: t.c.textSecondary, letterSpacing: "0.01em",
            }}>
              {label}
            </span>
          </div>
          <div style={{
            fontSize: valueSize, fontWeight: t.font.weight.medium, color: t.c.text,
            lineHeight: 1.15, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums",
          }}>
            {value}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, minHeight: 16 }}>
            {trend != null && <TrendPill value={trend} invert={trendInvert} />}
            {sublabel ? (
              <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>{sublabel}</span>
            ) : trend != null ? (
              <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>{trendLabel}</span>
            ) : null}
          </div>
        </div>
        <div style={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
          {gauge != null ? (
            <RadialGauge value={gauge} size={compact ? 40 : 48} stroke={5} color={gaugeColor || ac} label={typeof gauge === "number" ? `${Math.round(gauge)}%` : gauge} />
          ) : spark ? (
            <Sparkline data={spark} width={compact ? 64 : 88} color={sparkColor || ac} />
          ) : null}
        </div>
      </div>
    </Card>
  );
}

// ── Shimmer ──────────────────────────────────────────────────────────────────
export function Shimmer({ w = "100%", h = 14, radius = 6, style }) {
  const t = useDashTheme();
  return (
    <span
      style={{
        display: "block", width: w, height: h, borderRadius: radius,
        background: `linear-gradient(90deg, ${t.c.surfaceMuted} 25%, ${t.c.surfaceHover} 50%, ${t.c.surfaceMuted} 75%)`,
        backgroundSize: "200% 100%", animation: "dashv2-shimmer 1.4s ease-in-out infinite",
        ...style,
      }}
    />
  );
}
